// context/LoadingContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingContext = createContext();

export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error('useLoading must be used within LoadingProvider');
    }
    return context;
};

export const LoadingProvider = ({ children }) => {
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadedResources, setLoadedResources] = useState(0);
    const [totalResources, setTotalResources] = useState(0);
    const [currentStage, setCurrentStage] = useState('Initializing');

    // Enhanced resource loading with better error handling
    const loadResourceSafely = async (resource, type) => {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                resolve(false); // Resolve with false after timeout
            }, 3000); // 3 second timeout

            try {
                switch (type) {
                    case 'fonts':
                        const link = document.createElement('link');
                        link.rel = 'preload';
                        link.as = 'style';
                        link.href = resource;
                        link.onload = () => {
                            clearTimeout(timeout);
                            link.rel = 'stylesheet';
                            resolve(true);
                        };
                        link.onerror = () => {
                            clearTimeout(timeout);
                            resolve(false);
                        };
                        document.head.appendChild(link);
                        break;

                    case 'images':
                    case 'textures':
                        const img = new Image();
                        img.onload = () => {
                            clearTimeout(timeout);
                            resolve(true);
                        };
                        img.onerror = () => {
                            clearTimeout(timeout);
                            resolve(false);
                        };
                        img.src = resource;
                        break;

                    case 'videos':
                        // Skip video loading on mobile for performance
                        if (window.innerWidth <= 768) {
                            clearTimeout(timeout);
                            resolve(true);
                            return;
                        }

                        const video = document.createElement('video');
                        video.onloadedmetadata = () => {
                            clearTimeout(timeout);
                            resolve(true);
                        };
                        video.onerror = () => {
                            clearTimeout(timeout);
                            resolve(false);
                        };
                        video.preload = 'metadata';
                        video.src = resource;
                        break;

                    case 'models':
                        // Skip 3D models on mobile
                        if (window.innerWidth <= 768) {
                            clearTimeout(timeout);
                            resolve(true);
                            return;
                        }

                        fetch(resource, { method: 'HEAD' })
                            .then(() => {
                                clearTimeout(timeout);
                                resolve(true);
                            })
                            .catch(() => {
                                clearTimeout(timeout);
                                resolve(false);
                            });
                        break;

                    default:
                        clearTimeout(timeout);
                        resolve(true);
                }
            } catch (error) {
                clearTimeout(timeout);
                resolve(false);
            }
        });
    };

    // HOME-ONLY resource discovery with better mobile handling
    const homeResourceDiscovery = async () => {
        const discoveredResources = {
            fonts: [],
            images: [],
            videos: [],
            models: [],
            textures: []
        };

        const isMobile = window.innerWidth <= 768;

        try {
            // 1. Essential fonts only
            discoveredResources.fonts = [
                'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap'
            ];

            // 2. Load only essential home resources
            try {
                const { projects } = await import('../context/workConfig');

                // Only first 2 projects on mobile, 3 on desktop
                const homeProjectCount = isMobile ? 2 : 3;
                projects.slice(0, homeProjectCount).forEach((project) => {
                    if (project.mainImage && project.mainImage.startsWith('http')) {
                        discoveredResources.images.push(project.mainImage);
                    }
                });
            } catch (error) {
                console.warn('WorkConfig loading failed, continuing with essentials');
            }

        } catch (error) {
            console.warn('Resource discovery error:', error);
        }

        // Filter and validate URLs
        Object.keys(discoveredResources).forEach(key => {
            discoveredResources[key] = discoveredResources[key].filter(url =>
                url &&
                (url.startsWith('http') || url.startsWith('/')) &&
                !url.includes('data:') &&
                !url.includes('blob:')
            );
        });

        return discoveredResources;
    };

    const generateLoadingStages = (resources) => {
        const stages = [];
        let progressStart = 0;

        const resourceTypes = [
            { key: 'fonts', name: 'Loading fonts', weight: 30, baseTime: 200 },
            { key: 'images', name: 'Loading images', weight: 60, baseTime: 300 },
            { key: 'videos', name: 'Loading background', weight: 10, baseTime: 200 }
        ];

        const totalResources = Object.values(resources).reduce((sum, arr) => sum + arr.length, 0);

        if (totalResources === 0) {
            stages.push({
                name: 'Ready',
                resources: [],
                type: 'finalize',
                duration: 100,
                progressStart: 0,
                progressEnd: 100
            });
            return stages;
        }

        const totalWeight = resourceTypes.reduce((sum, type) => {
            return resources[type.key].length > 0 ? sum + type.weight : sum;
        }, 0);

        resourceTypes.forEach(type => {
            if (resources[type.key].length > 0) {
                const progressWeight = totalWeight > 0 ? (type.weight / totalWeight) * 90 : 0;
                const progressEnd = progressStart + progressWeight;

                stages.push({
                    name: type.name,
                    resources: resources[type.key],
                    type: type.key,
                    duration: type.baseTime,
                    progressStart: Math.round(progressStart),
                    progressEnd: Math.round(progressEnd)
                });

                progressStart = progressEnd;
            }
        });

        stages.push({
            name: 'Ready',
            resources: [],
            type: 'finalize',
            duration: 200,
            progressStart: Math.round(progressStart),
            progressEnd: 100
        });

        return stages;
    };

    useEffect(() => {
        const homeOnlyLoad = async () => {
            const hasVisited = sessionStorage.getItem('siteVisited');
            if (hasVisited) {
                setIsInitialLoading(false);
                return;
            }

            try {
                await new Promise(resolve => setTimeout(resolve, 200));

                setCurrentStage('Discovering assets');
                const discoveredResources = await homeResourceDiscovery();

                const total = Object.values(discoveredResources).reduce((sum, arr) => sum + arr.length, 0);
                setTotalResources(total);

                if (total === 0) {
                    // No resources to load, finish quickly
                    setCurrentStage('Ready');
                    for (let i = 0; i <= 100; i += 20) {
                        setLoadingProgress(i);
                        await new Promise(resolve => setTimeout(resolve, 30));
                    }
                    sessionStorage.setItem('siteVisited', 'true');
                    setIsInitialLoading(false);
                    return;
                }

                const stages = generateLoadingStages(discoveredResources);
                let resourcesLoaded = 0;

                for (const stage of stages) {
                    setCurrentStage(stage.name);

                    if (stage.type === 'finalize') {
                        const steps = 5;
                        const stepDuration = stage.duration / steps;
                        const progressIncrement = (stage.progressEnd - stage.progressStart) / steps;

                        for (let step = 0; step < steps; step++) {
                            const currentProgress = stage.progressStart + (progressIncrement * step);
                            setLoadingProgress(Math.min(currentProgress, 100));
                            await new Promise(resolve => setTimeout(resolve, stepDuration));
                        }
                    } else {
                        const resourcesInStage = stage.resources.length;
                        const progressPerResource = (stage.progressEnd - stage.progressStart) / Math.max(resourcesInStage, 1);

                        for (let i = 0; i < resourcesInStage; i++) {
                            const resource = stage.resources[i];

                            // Load resource with timeout and error handling
                            const loaded = await loadResourceSafely(resource, stage.type);

                            resourcesLoaded++;
                            setLoadedResources(resourcesLoaded);
                            const currentProgress = stage.progressStart + (progressPerResource * (i + 1));
                            setLoadingProgress(Math.min(currentProgress, stage.progressEnd));

                            await new Promise(resolve => setTimeout(resolve, 15));
                        }
                    }

                    await new Promise(resolve => setTimeout(resolve, 20));
                }

                setLoadingProgress(100);
                setCurrentStage('Experience ready');
                await new Promise(resolve => setTimeout(resolve, 300));

                sessionStorage.setItem('siteVisited', 'true');
                setIsInitialLoading(false);

            } catch (error) {
                console.warn('Loading error, completing anyway:', error);
                setLoadingProgress(100);
                setCurrentStage('Ready');
                sessionStorage.setItem('siteVisited', 'true');
                setIsInitialLoading(false);
            }
        };

        homeOnlyLoad();
    }, []);

    return (
        <LoadingContext.Provider value={{
            isInitialLoading,
            loadingProgress,
            loadedResources,
            totalResources,
            currentStage
        }}>
            <div className="relative">
                {children}
                <AnimatePresence>
                    {isInitialLoading && (
                        <motion.div
                            initial={{ opacity: 1 }}
                            exit={{
                                opacity: 0,
                                scale: 1.01,
                                filter: "blur(2px)"
                            }}
                            transition={{
                                duration: 0.8,
                                ease: [0.22, 1, 0.36, 1]
                            }}
                            className="fixed inset-0 bg-black z-[9999] flex items-center justify-center overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-gray-900/20" />

                            <div className="relative z-10 text-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{
                                        delay: 0.2,
                                        duration: 0.6,
                                        ease: [0.22, 1, 0.36, 1]
                                    }}
                                    className="mb-16"
                                >
                                    <div
                                        className="text-4xl font-black text-white tracking-wider relative"
                                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        <span className="inline-block">Paolo</span>
                                        <motion.span
                                            className="inline-block text-accent relative ml-1"
                                            animate={{
                                                textShadow: [
                                                    "0 0 0px rgba(255, 165, 0, 0)",
                                                    "0 0 20px rgba(255, 165, 0, 0.4)",
                                                    "0 0 0px rgba(255, 165, 0, 0)"
                                                ]
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        >
                                            A.
                                        </motion.span>
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{
                                        delay: 0.4,
                                        duration: 0.5,
                                        ease: [0.22, 1, 0.36, 1]
                                    }}
                                    className="relative"
                                >
                                    {/* Progress Bar - QUESTA È LA PARTE NUOVA */}
                                    <div className="relative w-64 h-1 mx-auto mb-8 bg-white/10 rounded-full overflow-hidden">
                                        <motion.div
                                            className="absolute top-0 left-0 h-full bg-accent rounded-full"
                                            animate={{
                                                width: `${loadingProgress}%`
                                            }}
                                            transition={{
                                                duration: 0.3,
                                                ease: "easeOut"
                                            }}
                                            style={{
                                                filter: "drop-shadow(0 0 8px rgba(255, 165, 0, 0.4))"
                                            }}
                                        />

                                        {/* Animated glow effect */}
                                        <motion.div
                                            className="absolute top-0 right-0 w-8 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                            animate={{
                                                x: ['-100%', '300%'],
                                                opacity: [0, 1, 0]
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                                repeatDelay: 1
                                            }}
                                            style={{
                                                transform: `translateX(${loadingProgress < 100 ? '-100%' : '300%'})`
                                            }}
                                        />
                                    </div>

                                    <motion.div
                                        key={currentStage}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                        className="text-white/45 text-xs font-light tracking-wider mb-4"
                                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {currentStage}
                                    </motion.div>

                                    <div className="flex items-center justify-center space-x-3 text-xs">
                                        <div className="text-white/40 font-light tabular-nums">
                                            {Math.round(loadingProgress)}%
                                        </div>
                                        {totalResources > 0 && (
                                            <>
                                                <div className="w-px h-2 bg-white/20" />
                                                <div className="text-white/30 font-light">
                                                    {loadedResources}/{totalResources}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </LoadingContext.Provider>
    );
};